/**
 * RoomList.tsx
 *
 * Presentational, opinionated list of a club's conversation rooms (#1179):
 * Lobby, Leaders, Happening Now, Coming Up, Series, Archive. Extracted from
 * the retired RoomSwitcherDrawer (#1976) so it can be re-presented inside the
 * header-opened ContextSheet instead of a low-discoverability edge drawer
 * (#856).
 *
 * Purely presentational: #1611 read-state/activity (`hasNewActivity` /
 * `isRecentlyActive`) is owned by ClubChatPage and passed down — calling the
 * activity hook here would create a stale, independent copy.
 */

import { useState, useMemo } from 'react';
import { MessageSquare, Calendar, Users, AlertCircle, ChevronRight, ChevronDown, Shield, Repeat } from 'lucide-react';
import type { Room } from '../hooks/useRooms';
import { getRoomCategory } from '../hooks/useRooms';
import { useNowSeconds } from '../hooks/useNowSeconds';

const MAX_HAPPENING_NOW = 2;
const MAX_COMING_UP_VISIBLE = 3;

interface RoomListProps {
  rooms: Room[];
  activeRoomId: string | null;
  onSelectRoom: (roomId: string) => void;
  isLoading?: boolean;
  error?: Error | null;
  // #1611: Activity utilities passed in from ClubChatPage so a single
  // useRoomActivity instance owns the state.
  hasNewActivity: (room: Room) => boolean;
  isRecentlyActive: (room: Room) => boolean;
}

export default function RoomList({
  rooms,
  activeRoomId,
  onSelectRoom,
  isLoading = false,
  error = null,
  hasNewActivity,
  isRecentlyActive,
}: RoomListProps) {
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const [comingUpExpanded, setComingUpExpanded] = useState(false);

  // #1693: live nowSeconds keeps categorization (happening-now / coming-up /
  // archive) current on long-lived sessions — 60s ticks re-run the useMemo.
  const now = useNowSeconds();

  // Categorize rooms by type and time (#1179 opinionated layout, #1098 leaders, #1216 series)
  const categorizedRooms = useMemo(() => {
    const result = {
      lobby: null as Room | null,
      leaders: null as Room | null,
      happeningNow: [] as Room[],
      comingUp: [] as Room[],
      trainingGroups: [] as Room[],
      series: [] as Room[],
      archive: [] as Room[],
    };

    rooms.forEach((room) => {
      if (room.is_leaders_only) {
        result.leaders = room;
        return;
      }

      if (room.room_type === 'club_lobby') {
        result.lobby = room;
        return;
      }

      const category = getRoomCategory(room, now);

      if (room.room_type === 'training_group_room') {
        if (category === 'archive') {
          result.archive.push(room);
        } else {
          result.trainingGroups.push(room);
        }
      } else if (room.room_type === 'series_room') {
        if (category === 'active') {
          result.happeningNow.push(room);
        } else if (category === 'upcoming') {
          result.series.push(room);
        } else {
          result.archive.push(room);
        }
      } else if (room.room_type === 'event_room') {
        if (category === 'active') {
          result.happeningNow.push(room);
        } else if (category === 'upcoming') {
          result.comingUp.push(room);
        } else {
          result.archive.push(room);
        }
      }
    });

    const getStartTime = (r: Room) => r.event_starts_at || r.training_group_start_date || r.series_next_event || 0;
    const getEndTime = (r: Room) => r.event_ends_at || r.training_group_end_date || 0;

    result.happeningNow.sort((a, b) => getStartTime(a) - getStartTime(b));
    result.comingUp.sort((a, b) => getStartTime(a) - getStartTime(b));
    result.trainingGroups.sort((a, b) => getStartTime(a) - getStartTime(b));
    result.series.sort((a, b) => getStartTime(a) - getStartTime(b));
    result.archive.sort((a, b) => getEndTime(b) - getEndTime(a)); // Most recent first

    return result;
  }, [rooms, now]);

  const { lobby, leaders, happeningNow, comingUp, series, trainingGroups, archive } = categorizedRooms;

  const visibleHappeningNow = happeningNow.slice(0, MAX_HAPPENING_NOW);
  const overflowHappeningNow = happeningNow.length - MAX_HAPPENING_NOW;
  const visibleComingUp = comingUpExpanded ? comingUp : comingUp.slice(0, MAX_COMING_UP_VISIBLE);
  const hiddenComingUp = comingUp.length - MAX_COMING_UP_VISIBLE;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-text-secondary">Loading conversations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="flex items-start gap-3 p-4 bg-oro-error-soft border border-oro-error-border rounded-lg">
          <AlertCircle className="w-5 h-5 text-oro-error flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-medium text-text-primary">Unable to load conversations</div>
            <div className="text-sm text-text-secondary mt-1">{error.message || 'Please try again later.'}</div>
          </div>
        </div>
      </div>
    );
  }

  const isEmpty =
    !lobby && !leaders && happeningNow.length === 0 && comingUp.length === 0 &&
    series.length === 0 && trainingGroups.length === 0 && archive.length === 0;

  return (
    // Scrolling is owned by the parent BottomSheet's content region (#1976 Phase 2)
    // so the Clubs section and rooms scroll as one list. Keep data-tour here — the
    // Joyride 'chat-rooms-list' step targets it (Core Rule 35).
    <div data-tour="chat-rooms-list">
      {/* LOBBY - Always prominent */}
      {lobby && (
        <div className="border-b border-oro-border">
          <RoomItem
            room={lobby}
            isActive={activeRoomId === lobby.id}
            onSelect={() => onSelectRoom(lobby.id)}
            icon={<MessageSquare className="w-5 h-5" />}
            hasActivity={hasNewActivity(lobby)}
            isLive={isRecentlyActive(lobby)}
            prominent
          />
        </div>
      )}

      {/* LEADERS - Only visible to owners/leaders (#1098) */}
      {leaders && (
        <div className="border-b border-oro-border">
          <SectionHeader label="Leaders Only" icon={<Shield className="w-3 h-3" />} />
          <RoomItem
            room={leaders}
            isActive={activeRoomId === leaders.id}
            onSelect={() => onSelectRoom(leaders.id)}
            icon={<Shield className="w-5 h-5" />}
            hasActivity={hasNewActivity(leaders)}
            isLive={isRecentlyActive(leaders)}
          />
        </div>
      )}

      {/* HAPPENING NOW - Active events (max 2) */}
      {happeningNow.length > 0 && (
        <div className="border-b border-oro-border">
          <SectionHeader label="Happening Now" />
          {visibleHappeningNow.map((room) => (
            <RoomItem
              key={room.id}
              room={room}
              isActive={activeRoomId === room.id}
              onSelect={() => onSelectRoom(room.id)}
              icon={<Calendar className="w-5 h-5" />}
              hasActivity={hasNewActivity(room)}
              isLive={isRecentlyActive(room)}
            />
          ))}
          {overflowHappeningNow > 0 && (
            <div className="px-4 py-2 text-xs text-text-muted">+{overflowHappeningNow} more live</div>
          )}
        </div>
      )}

      {/* COMING UP - Upcoming events (collapsed) */}
      {comingUp.length > 0 && (
        <div className="border-b border-oro-border">
          <SectionHeader label="Coming Up" />
          {visibleComingUp.map((room) => (
            <RoomItem
              key={room.id}
              room={room}
              isActive={activeRoomId === room.id}
              onSelect={() => onSelectRoom(room.id)}
              icon={<Calendar className="w-5 h-5" />}
              hasActivity={hasNewActivity(room)}
              isLive={isRecentlyActive(room)}
              timeHint={getRelativeTimeHint(room.event_starts_at)}
            />
          ))}
          {hiddenComingUp > 0 && (
            <button
              onClick={() => setComingUpExpanded(!comingUpExpanded)}
              className="w-full flex items-center gap-2 px-4 py-2 text-xs text-text-muted hover:text-text-secondary transition-colors"
            >
              <ChevronDown className={`w-3 h-3 transition-transform ${comingUpExpanded ? '' : '-rotate-90'}`} />
              {comingUpExpanded ? 'Show less' : `${hiddenComingUp} more this week`}
            </button>
          )}
        </div>
      )}

      {/* SERIES - Recurring event series (#1216) */}
      {series.length > 0 && (
        <div className="border-b border-oro-border">
          <SectionHeader label="Series" icon={<Repeat className="w-3 h-3" />} />
          {series.map((room) => (
            <RoomItem
              key={room.id}
              room={room}
              isActive={activeRoomId === room.id}
              onSelect={() => onSelectRoom(room.id)}
              icon={<Repeat className="w-5 h-5" />}
              hasActivity={hasNewActivity(room)}
              isLive={isRecentlyActive(room)}
              timeHint={getRelativeTimeHint(room.series_next_event)}
            />
          ))}
        </div>
      )}

      {/* TRAINING GROUPS - Hidden for v1.2 release (#1241) */}

      {/* ARCHIVE - Entry point only (#1179: not a list, just a door) */}
      {archive.length > 0 && (
        <div>
          <button
            onClick={() => setArchiveExpanded(!archiveExpanded)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-oro-hover transition-colors"
          >
            <span className="text-sm text-text-muted">Archive</span>
            <ChevronRight className={`w-4 h-4 text-text-muted transition-transform ${archiveExpanded ? 'rotate-90' : ''}`} />
          </button>
          {archiveExpanded && (
            <div className="bg-oro-surface-alt">
              {archive.map((room) => (
                <RoomItem
                  key={room.id}
                  room={room}
                  isActive={activeRoomId === room.id}
                  onSelect={() => onSelectRoom(room.id)}
                  icon={getRoomIcon(room)}
                  hasActivity={hasNewActivity(room)}
                  isLive={isRecentlyActive(room)}
                  muted
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <MessageSquare className="w-12 h-12 text-text-muted mb-4" />
          <div className="text-text-secondary">No conversations available</div>
        </div>
      )}
    </div>
  );
}

// Room list item (#1180: Lantern activity indicators)
interface RoomItemProps {
  room: Room;
  isActive: boolean;
  onSelect: () => void;
  icon: React.ReactNode;
  hasActivity?: boolean;
  isLive?: boolean;
  prominent?: boolean;
  muted?: boolean;
  timeHint?: string | null;
}

function RoomItem({
  room,
  isActive,
  onSelect,
  icon,
  hasActivity = false,
  isLive = false,
  prominent = false,
  muted = false,
  timeHint,
}: RoomItemProps) {
  // #1219 type-aware fallback for rooms without display_name; #1281 Leaders label
  const displayName = room.display_name || (
    room.is_leaders_only ? 'Leaders' :
    room.room_type === 'club_lobby' ? 'Lobby' :
    room.room_type === 'event_room' ? 'Event' :
    room.room_type === 'training_group_room' ? 'Group' :
    room.room_type === 'series_room' ? 'Series' :
    'Chat'
  );

  const lanternClasses = !isActive && hasActivity
    ? isLive ? 'lantern-activity lantern-live' : 'lantern-activity'
    : '';

  return (
    <button
      onClick={onSelect}
      className={`
        w-full flex items-center gap-3 px-4 py-3 min-h-11
        transition-colors
        ${isActive ? 'bg-oro-accent/10 border-r-4 border-oro-accent' : 'border-r-4 border-transparent hover:bg-oro-hover'}
        ${prominent ? 'py-4' : ''}
      `}
      aria-current={isActive ? 'page' : undefined}
    >
      <div
        className={`
          relative flex-shrink-0 rounded-full p-1
          ${isActive ? 'text-oro-accent' : muted ? 'text-text-muted' : 'text-text-secondary'}
          ${lanternClasses}
        `}
      >
        {icon}
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className={`truncate ${prominent ? 'font-semibold' : 'font-medium'} ${muted ? 'text-text-muted' : 'text-text-primary'}`}>
          {displayName}
        </div>
      </div>
      {timeHint && <span className="text-xs text-text-muted flex-shrink-0">{timeHint}</span>}
    </button>
  );
}

function SectionHeader({ label, icon }: { label: string; icon?: React.ReactNode }) {
  return (
    <div className="px-4 py-2 bg-oro-surface-alt">
      <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wider flex items-center gap-1.5">
        {icon}
        {label}
      </h3>
    </div>
  );
}

function getRoomIcon(room: Room): React.ReactNode {
  switch (room.room_type) {
    case 'club_lobby':
      return <MessageSquare className="w-5 h-5" />;
    case 'event_room':
      return <Calendar className="w-5 h-5" />;
    case 'training_group_room':
      return <Users className="w-5 h-5" />;
    case 'series_room':
      return <Repeat className="w-5 h-5" />;
    default:
      return <MessageSquare className="w-5 h-5" />;
  }
}

// Relative time hint (e.g., "2d", "5h", "Tomorrow")
function getRelativeTimeHint(startsAt: number | null | undefined): string | null {
  if (!startsAt) return null;
  const now = Math.floor(Date.now() / 1000);
  const diff = startsAt - now;
  if (diff < 0) return null;
  if (diff < 3600) return `${Math.ceil(diff / 60)}m`;
  if (diff < 86400) return `${Math.ceil(diff / 3600)}h`;
  if (diff < 172800) return 'Tomorrow';
  return `${Math.ceil(diff / 86400)}d`;
}
